import './assets/general.css'

import { defineCustomElement } from 'vue'
import KCFrontendApp from './App.vue'
import { vuetify } from './plugins/vuetify'
import { createPinia } from 'pinia'
import { i18n } from './i18n'
import * as Sentry from '@sentry/vue'

const pinia = createPinia()

// If it is not defined in the configmap, window.SENTRY_HOST_URL is rendered as the literal string "${env.SENTRY_HOST_URL}" :(
function validateSentryUrl(url: string | undefined) {
  if (!url) {
    return undefined
  }
  try {
    new URL(url)
    return url
  } catch {
    return undefined
  }
}

function validateVersion(version: string | undefined) {
  // Regex from https://semver.org/ (bottom of the page)
  if (
    version &&
    /^(0|[1-9]\d*)\.(0|[1-9]\d*)\.(0|[1-9]\d*)(?:-((?:0|[1-9]\d*|\d*[a-zA-Z-][0-9a-zA-Z-]*)(?:\.(?:0|[1-9]\d*|\d*[a-zA-Z-][0-9a-zA-Z-]*))*))?(?:\+([0-9a-zA-Z-]+(?:\.[0-9a-zA-Z-]+)*))?$/.test(
      version,
    )
  ) {
    return version
  } else {
    return undefined
  }
}

const sentryHostUrl = validateSentryUrl((window as any).SENTRY_HOST_URL) // eslint-disable-line @typescript-eslint/no-explicit-any

const KCFrontendAppCustomElement = defineCustomElement(KCFrontendApp, {
  shadowRoot: false,
  configureApp(app) {
    app.use(vuetify)
    app.use(i18n)
    app.use(pinia)

    if (sentryHostUrl) {
      Sentry.init({
        app,
        dsn: sentryHostUrl,
        release: validateVersion((window as any).KEYCLOAK_EXTENSIONS_VERSION), // eslint-disable-line @typescript-eslint/no-explicit-any
        integrations: [Sentry.replayIntegration()],
        // Tracing
        tracesSampleRate: 1.0, //  Capture 100% of the transactions
        // Set 'tracePropagationTargets' to control for which URLs distributed tracing should be enabled
        tracePropagationTargets: [
          'localhost',
          /^https:\/\/yourserver\.io\/api/,
        ],
        // Session Replay
        replaysSessionSampleRate: 0.1, // This sets the sample rate at 10%. You may want to change it to 100% while in development and then sample at a lower rate in production.
        replaysOnErrorSampleRate: 1.0, // If you're not already sampling the entire session, change the sample rate to 100% when sampling sessions where errors occur.

        // https://github.com/getsentry/sentry-javascript/issues/3440#issuecomment-828834651
        beforeSend(event) {
          // filter out UnhandledRejection errors that have no information
          if (
            event !== undefined &&
            event.exception !== undefined &&
            event.exception.values !== undefined &&
            event.exception.values.length == 1
          ) {
            const e = event.exception.values[0]
            if (
              e.type === 'UnhandledRejection' &&
              e.value === 'Non-Error promise rejection captured with value: '
            ) {
              return null
            }
          }
          return event
        },
      })
    }
  },
})

customElements.define('kc-app', KCFrontendAppCustomElement)
